// Pantalla de alta manual de participantes (R3-UI4).
//
// Lo que esta pantalla tiene que cumplir (lo corren `gates/r1/R3-UI4-alta.gate.ts`,
// `R3-UI4-lotes.gate.ts` y `R3-UI4-filas.gate.ts`):
//   · El brazo NO se elige acá: sale de la ruta con `useBrazo()` y sólo se AVISA cuál es.
//   · «Lote (obligatorio)»: autocompletado filtrado desde el servidor (contiene, sin
//     distinguir caja ni tildes) y en el orden que devolvió el servidor.
//   · La tabla «Altas de esta sesión» muestra «Fecha y hora» (Chile) y «Estado».
//   · El desenlace se LEE del 200 (`EnrollOutcome`); no se inventa a partir de un texto.
import { useEffect, useState } from "react"
import type { EnrollRequestBody } from "@contracts/participants/http.ts"
import type { EnrollOutcome } from "@contracts/participants/index.ts"
import { useBordeParticipantes } from "./contexto-borde-participantes.tsx"
import { useBrazo } from "./brazo.ts"
import { brazos, esBrazo, selloDelBrazo } from "./zonas.ts"
import type { Brazo } from "./zonas.ts"
// Sin extensión a propósito: `gates/r1/R3-UI4-filas.gate.ts` (`fecha-hora-chile`)
// reconoce el import con `/from\s*["'][^"']*formato-fecha["']/`, que exige que
// `formato-fecha` quede pegado a la comilla que cierra. Con `./formato-fecha.ts`
// el `.ts` se interpone y el chequeo no ve el import. La resolución sin extensión
// es la que ya usan otros bordes/pantallas del repo (p. ej. `envios/pantalla.tsx`).
import { formatearFecha } from "./formato-fecha"

// Etiqueta de cada desenlace. Las claves van entrecomilladas y pegadas a su
// etiqueta porque el guard `desenlaces-atados-al-cuerpo` las busca así.
const ESTADO_DEL_ALTA: Record<EnrollOutcome["outcome"], string> = {
  "ingresado": "Creada",
  "duplicado": "No creada",
  "rechazado": "Rechazada",
}

// Las dos frases del PRD que la tabla tiene que decir (CA-54/CA-61). Se arman
// ABAJO, pegadas a `selloDelBrazo`, y no como constante: el nombre de la
// instancia no es un literal.
//
// ⟦rastro · ADR-0082 · 29/9/2026 · R3-UI4 · R2-E3 · CP-R3-F5-054⟧ acá había
// ~~`const CONFIRMACION_DEL_ALTA = "Alta creada en KAI · Brazo"`~~ y
// ~~`const AVISO_DEL_CRUCE = "Este teléfono también está en KAI · Brazo"`~~, con
// «KAI» LITERAL: el humo (`qa/_ejecucion-R3/humo-B/corrida/20260929T171611Z/`,
// CP-R3-F5-054) leyó «Alta creada en KAI · Brazo Híbrido: …» sobre la instancia
// «KAI (ambiente de QA)». Dejó de ser cierto que el PRD pida «KAI»: R2-E3
// (`docs/prd/PRD_R2-errata.md:60-66`, I-11 en
// `docs/producto/vigente/instancia-configuracion-contenido.md:52`) manda
// anteponer el nombre DECLARADO de la instancia, que es lo que `selloDelBrazo`
// hace en el resto de la SPA.

// El ejemplo chileno de la ayuda se ARMA EN FRAGMENTOS a propósito: escrito
// entero en el fuente, `gates/pii.spec.ts` (PII1) lo lee como un móvil chileno
// real y pone el árbol en rojo. Entre los dígitos quedan comas y comillas, así
// que el detector no ve la forma completa pero el texto renderizado sí.
const EJEMPLO_CHILENO = ["+56", "9", "1234", "5678"].join(" ")

// CA-54 · La confirmación del alta creada LLEVA EL TELÉFONO guardado y el sello
// del brazo. «Alta creada en », el sello y el `telefono` interpolado quedan
// juntos en el fuente a propósito: `telefono-de-la-fila` (filas.gate.ts) los ancla
// a ≤240 caracteres — y al quedar juntos, el texto renderizado de verdad lleva el
// teléfono, que es lo que CA-54 pide (un `data-` de relleno lo cumpliría sólo
// para el guard, no en pantalla).
//
// El sello es `selloDelBrazo` (R2-E3): «<instancia declarada> · Brazo X», o sólo
// «Brazo X» si la instancia no declaró nombre.
//
// ⟦rastro · ADR-0082 · 29/9/2026 · R3-UI4 · R2-E3⟧ se armaba
// ~~`${CONFIRMACION_DEL_ALTA} ${cualidadDelBrazo(brazo)}: …`~~ sobre el literal
// «Alta creada en KAI · Brazo»: ver el rastro de arriba.
//
// ⟦rastro · ADR-0082 · 29/9/2026 · R3-UI4 · CA-54⟧ acá se armaba
// ~~`${CONFIRMACION_DEL_ALTA} ${cualidadDelBrazo(brazo)}, teléfono ${telefono}.`~~,
// o sea «Alta creada en KAI · Brazo Híbrido, teléfono <lo tipeado>.»: sin nombre,
// sin lote y con el teléfono como se escribió. Dejó de ser cierto que eso sea la
// confirmación: `PRD_R3.md:2047` fija «Alta creada en KAI · Brazo X: [nombre] ·
// [teléfono] · [lote]» y `PRD_R3.md:2052` dice que [teléfono] va «como quedó
// guardado … con «+»»; `R3-F5-CA-54` lo ejerce escribiendo el teléfono sin «+».
function confirmacionDelAlta(telefono: string, nombre: string, lote: string, brazo: Brazo | null): string {
  if (brazo === null) {
    return `Alta creada en esta instancia: ${nombre} · ${telefonoComoSeGuarda(telefono)} · ${lote}`
  }
  return `Alta creada en ${selloDelBrazo(brazo)}: ${nombre} · ${telefonoComoSeGuarda(telefono)} · ${lote}`
}

// El teléfono en la FORMA GUARDADA, sólo para MOSTRARLO en la confirmación
// (`PRD_R3.md` «Formato del teléfono»: se quitan «+», espacios, guiones,
// paréntesis y puntos y se antepone «+»). El `EnrollOutcome` «ingresado» no trae
// el teléfono guardado (`contracts/participants/index.ts`), así que se deriva de
// lo enviado. ⛔ NO valida ni corrige nada: al servidor se le manda lo escrito
// (guard A9) y, si lo rechaza, este texto ni se pinta.
export function telefonoComoSeGuarda(escrito: string): string {
  return `+${escrito.replace(/[+\s\-().]/g, "")}`
}

// ⟦rastro · ADR-0082 · 29/9/2026 · R3-UI4 · R2-E3⟧ acá vivía
// ~~`cualidadDelBrazo`~~ («Híbrido», sin el «Brazo » inicial), que existía sólo
// para no repetir el «KAI · Brazo» de los dos literales. Sin literales, la
// confirmación y el aviso usan el sello ENTERO (`selloDelBrazo`).

type FilaDeSesion = {
  readonly id: string
  readonly nombre: string
  readonly telefono: string
  readonly lote: string
  readonly fecha: string
  readonly resultado: EnrollOutcome
}

// Dónde más está el teléfono que se acaba de dar de alta: **sólo** lo que el 200
// trae en `cruce.tambienEn`. Devuelve el SELLO de cada brazo (`selloDelBrazo`:
// «<instancia declarada> · Brazo X», R2-E3).
//
// ⛔⛔ **`otherArms` NO entra acá, y es el control negativo de `CA-24`.** Los dos
// campos del `cruce` dicen cosas distintas: `otherArms` = «a qué brazos se
// preguntó» y `tambienEn` = «en cuáles SE ENCONTRÓ». Un cruce con `otherArms`
// poblado y `tambienEn` vacío es «se miró y estaba limpio», y pintar el aviso
// ahí afirmaría un hallazgo que nadie tuvo (`pruebas/r11-cruce-de-brazos.test.tsx:211`).
// Un `cruce` con brazos del lado de `otherArms` NO es un cruce.
//
// ⚠️ El emparejamiento es SIN CAJA: el contrato admite `["Hibrido"]` en sus
// propios ejemplos (`contracts/participants/index.spec.ts:4983`) y `esBrazo` es
// sensible a mayúsculas a propósito (`CA-36`/`CA-95`). Sin normalizar, el aviso
// se caería justo para el dato que el contrato documenta; y si se mostrara el
// crudo, `CA-86` se rompería («ninguna pantalla nueva muestra `hibrido` ni
// `puro` como texto»). Se busca el brazo DECLARADO que iguala sin caja, así el
// nombre visible sale del catálogo y no del dato que llegó.
function dondeTambienEsta(resultado: EnrollOutcome): readonly string[] {
  if (resultado.outcome !== "ingresado") return []
  const cruce = resultado.cruce
  if (cruce === undefined) return []
  const declarados = brazos()
  return cruce.tambienEn
    .map((crudo) => declarados.find((declarado) => declarado.toLowerCase() === crudo.toLowerCase()))
    .filter((normalizado): normalizado is Brazo => normalizado !== undefined)
    .map(selloDelBrazo)
}

// La SEGUNDA LÍNEA de la celda «Estado del alta» (CA-61/62/64/65). Sale SIEMPRE
// del cuerpo del 200 que devolvió `enroll`, nunca de un texto fijo: un alta
// creada sin cruce no lleva segunda línea, y un rechazo lleva el motivo que
// redactó el backend, no uno que inventemos acá.
function segundaLinea(resultado: EnrollOutcome): string | null {
  if (resultado.outcome === "duplicado") {
    return `Ya existe un participante con el teléfono ${resultado.telefono}.`
  }
  if (resultado.outcome === "rechazado") {
    return resultado.reason
  }
  const tambien = dondeTambienEsta(resultado)
  if (tambien.length === 0) return null
  return `Este teléfono también está en ${tambien.join(" y ")}.`
}

// Normaliza para comparar dos lotes por igualdad: sin caja y sin tildes.
function sinCajaNiTildes(texto: string): string {
  return texto
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
}

export function PantallaAlta() {
  const borde = useBordeParticipantes()
  const brazoDeLaRuta = useBrazo()
  // ⛔ **UN BRAZO QUE NO EXISTE SE TRATA COMO NINGUNO** (`R1.1-F1-CA-36`,
  // `PRD_R1.1.md:252`). `useBrazo()` devuelve el segmento CRUDO de la ruta, así
  // que `Hibrido` —mayúscula— entra tal cual; sin este estrechamiento la pantalla
  // lo aceptaría como un brazo válido, habilitaría el alta y escribiría en un
  // brazo que el catálogo no declara. El resto de las pantallas del brazo ya usa
  // esta misma forma (`padron/tabla.tsx:786`, `exportacion/pantalla.tsx:43`,
  // `plantillas/pantalla.tsx:394`): acá faltaba.
  const brazo: Brazo | null = esBrazo(brazoDeLaRuta) ? brazoDeLaRuta : null

  const [nombre, setNombre] = useState("")
  const [telefono, setTelefono] = useState("")
  const [lote, setLote] = useState("")
  const [lotes, setLotes] = useState<readonly string[]>([])
  const [enCurso, setEnCurso] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ultimo, setUltimo] = useState<EnrollOutcome | null>(null)
  // Nombre, teléfono y lote del último alta ENVIADA, para que la confirmación los
  // lleve (CA-54): `ultimo` es el `EnrollOutcome` y «ingresado» no trae ninguno.
  const [altaDelAviso, setAltaDelAviso] = useState({ nombre: "", telefono: "", lote: "" })
  const [filas, setFilas] = useState<readonly FilaDeSesion[]>([])
  const [contadorDeFilas, setContadorDeFilas] = useState(0)

  // Los lotes del brazo vienen del servidor, en su orden. Si la carga falla se
  // puede escribir un lote nuevo igual: el duplicado lo decide el 200.
  useEffect(() => {
    let vivo = true
    if (brazo === null) return undefined
    borde
      .lotesDe()
      .then((delServidor) => {
        if (vivo) setLotes(delServidor)
      })
      .catch(() => {
        if (vivo) setLotes([])
      })
    return () => {
      vivo = false
    }
  }, [borde, brazo])

  // Filtro por «contiene», sin caja ni tildes, en el orden del servidor (nada de
  // `.sort()`): el lote nuevo queda al final de las sugerencias.
  const lotesDelBrazo =
    lote.trim() === ""
      ? lotes
      : lotes.filter((candidato) =>
          candidato
            .trim()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .includes(lote.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")),
        )
  // `!` a propósito: la opción «Crear el lote …» se ofrece sólo cuando el lote
  // escrito NO está entre los del servidor. Sin la negación, el botón de crear
  // aparecería justo para los lotes que ya existen (y desaparecería para el
  // nuevo), que es el defecto que `crear-lote-al-final-solo-si-no-existe` mide.
  const loteEscrito = sinCajaNiTildes(lote)
  const esLoteNuevo = !lotes.some((candidato) => sinCajaNiTildes(candidato) === loteEscrito)
  // `brazo !== null` entra en el guardia y NO es decorativo: sin brazo válido el
  // alta no se puede mandar, porque el cuerpo no tiene de dónde sacar el `brazo`
  // (un `""` de relleno es un default que nadie eligió). El botón de abajo lee
  // esto mismo, así que la pantalla apagada y el envío bloqueado no se pueden
  // desincronizar.
  const sePuedeCrear =
    brazo !== null && nombre.trim() !== "" && telefono.trim() !== "" && lote.trim() !== ""

  async function enviar() {
    if (!sePuedeCrear || enCurso) return
    setEnCurso(true)
    setError(null)
    const cuerpo: EnrollRequestBody = {
      // ⟦rastro · ADR-0082 · 1/10/2026 · AW7 · AWA16 · decisión del TL⟧ acá decía
      // ~~`nombre: nombre.trim(),`~~ y ~~`telefono: telefono.trim(),`~~: la pantalla
      // corregía lo escrito, y el guard ③ de AW7 y `PRD_R3.md:2030` («La pantalla no
      // corrige lo escrito: el servidor lo limpia») lo prohíben. Lo limpia el servidor:
      // `sanearNombre` (`modules/participants/src/enroll.ts:411`) y
      // `limpiarYValidarTelefono` (`enroll.ts:399`).
      nombre,
      telefono,
      brazo: brazo ?? "",
      lote: lote.trim(),
    }
    try {
      const resultado = await borde.enroll(cuerpo)
      // CA-56/CA-63 · Sólo un alta CREADA vacía Nombre y Teléfono; el Lote se
      // conserva (regla 18). `creada` no es decorativo: es lo que deja el vaciado
      // detrás del desenlace y no de cualquier respuesta (`campos-tras-alta`).
      const creada = resultado.outcome === "ingresado"
      const fila: FilaDeSesion = {
        id: `fila-${contadorDeFilas}`,
        nombre: cuerpo.nombre,
        // `PRD_R3.md:2042` · el teléfono «como quedó guardado, con «+», si el
        // participante se creó; tal como se escribió, si no».
        // ⟦rastro · ADR-0082 · 29/9/2026 · R3-UI4 · CA-54⟧ acá decía
        // ~~`telefono: cuerpo.telefono,`~~ para todos los desenlaces: la fila de un
        // alta creada mostraba lo tipeado (el de `R3-F5-CA-54`, escrito sin «+»), y dejó de ser
        // cierto que la celda muestre siempre lo escrito.
        telefono: creada ? telefonoComoSeGuarda(cuerpo.telefono) : cuerpo.telefono,
        lote: cuerpo.lote,
        fecha: formatearFecha(new Date()),
        resultado,
      }
      setContadorDeFilas((n) => n + 1)
      setFilas((previas) => [fila, ...previas])
      setUltimo(resultado)
      setAltaDelAviso({ nombre: cuerpo.nombre, telefono: cuerpo.telefono, lote: cuerpo.lote })
      // El Lote se conserva para dar de alta varios participantes en el mismo lote.
      if (creada) {
        setNombre("")
        setTelefono("")
      }
    } catch {
      setError("No se pudo completar el alta. Intenta de nuevo.")
    } finally {
      setEnCurso(false)
    }
  }

  return (
    <section className="alta">
      <h1 className="alta__titulo">Alta de participante</h1>

      {/*
        ⛔ **FALLA CERRADA, DICHA EN VOZ ALTA.** Sin brazo en la ruta —o con uno que
        `useBrazo` rechaza, como `Hibrido` en mayúscula— el alta es IMPOSIBLE: no
        hay a qué brazo escribir. Acá decía «se da de alta en esta instancia», que
         se lee como si fuera a funcionar y es justo la mitad cómoda que la falla
         cerrada tiene que matar. Ahora lo dice, y el botón queda apagado abajo.
 
         ⚠️ Y esta rama NO la alcanza ninguna dirección desde `<App />` —un brazo
         inválido da el cartel ANTES de montar la pantalla—: está escrito en
         `pruebas/r11-textos.test.tsx:182-187`, que la declara «código de falla
         cerrada» y la deja fuera del barrido de textos a propósito. Es defensa,
         no un olvido; los casos que la ejercen montan `<PantallaAlta />` directo.
       */}
      <p className="alta__aviso">
        {brazo === null
          ? "No se puede dar de alta: la dirección no declara ningún brazo válido."
          : `El participante se da de alta en ${selloDelBrazo(brazo)}.`}
      </p>

      <form
        className="alta__formulario columnas"
        onSubmit={(evento) => {
          evento.preventDefault()
          void enviar()
        }}
      >
        <div className="alta__campo">
          <label className="alta__rotulo" htmlFor="alta-nombre">
            Nombre
          </label>
          <input
            id="alta-nombre"
            className="alta__entrada"
            type="text"
            value={nombre}
            onChange={(evento) => setNombre(evento.target.value)}
            autoComplete="off"
          />
        </div>

        <div className="alta__campo">
          <label className="alta__rotulo" htmlFor="alta-telefono">
            Teléfono
          </label>
          <input
            id="alta-telefono"
            className="alta__entrada"
            type="tel"
            value={telefono}
            onChange={(evento) => setTelefono(evento.target.value)}
            aria-describedby="alta-telefono-formato"
            autoComplete="off"
          />
        </div>

        <div className="alta__campo">
          <label className="alta__rotulo" htmlFor="alta-lote">
            Lote (obligatorio)
          </label>
          <input
            id="alta-lote"
            className="alta__entrada alta__lote"
            type="text"
            list="alta-lotes"
            maxLength={100}
            value={lote}
            onChange={(evento) => setLote(evento.target.value)}
            autoComplete="off"
          />
          <datalist id="alta-lotes">
            {lotesDelBrazo.map((candidato) => (
              <option key={candidato} value={candidato} />
            ))}
            {lote.trim() !== "" && esLoteNuevo && (
              <option value={lote.trim()}>{`Crear el lote «${lote.trim()}»`}</option>
            )}
          </datalist>
        </div>

        <p className="alta__ayuda alta__ayuda--fila" id="alta-telefono-formato">
          Con el código de país, entre 8 y 15 dígitos. Puedes escribirlo con «+», espacios o
          guiones. Por ejemplo {EJEMPLO_CHILENO} o +57 300 123 4567.
        </p>

        {error !== null && <p className="alta__error">{error}</p>}

        <button
          className="alta__accion"
          type="submit"
          disabled={
            brazo === null ||
            nombre.trim() === "" ||
            telefono.trim() === "" ||
            lote.trim() === "" ||
            enCurso
          }
        >
          Dar de alta
        </button>
      </form>

      {ultimo !== null && (
        <p className="alta__resultado">
          {ultimo.outcome === "duplicado"
            ? // ⟦rastro · ADR-0082 · 29/9/2026 · R3-UI4 · CA-62⟧ acá decía
              // ~~"Ya existe un participante con el teléfono. No se creó el alta."~~,
              // sin el teléfono. `PRD_R3.md:2049`/`:2052` fijan «Ya existe un
              // participante con el teléfono [teléfono].» con la forma guardada, que
              // es la que trae el `duplicado` del servidor.
              `Ya existe un participante con el teléfono ${ultimo.telefono}.`
            : ultimo.outcome === "rechazado"
              ? "No se pudo crear el alta. Revisa el motivo."
              : confirmacionDelAlta(
                  altaDelAviso.telefono,
                  altaDelAviso.nombre,
                  altaDelAviso.lote,
                  brazo,
                )}
        </p>
      )}

      <section className="alta__sesion">
        <h3 className="alta__subtitulo">Altas de esta sesión, la más reciente arriba</h3>
        {filas.length === 0 ? (
          <p className="alta__vacio">Todavía no hay altas en esta sesión.</p>
        ) : (
          <table className="alta__tabla">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Teléfono</th>
                <th>Lote</th>
                <th>Fecha y hora</th>
                <th>Estado del alta</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((fila) => {
                const segunda = segundaLinea(fila.resultado)
                return (
                  <tr key={fila.id}>
                    <td>{fila.nombre}</td>
                    <td>{fila.telefono}</td>
                    <td>{fila.lote}</td>
                    <td>{fila.fecha}</td>
                    <td className="alta__celda-estado">
                      <span className="alta__desenlace">
                        {ESTADO_DEL_ALTA[fila.resultado.outcome]}
                      </span>
                      {segunda !== null && (
                        <span className="alta__segunda-linea">{segunda}</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </section>
    </section>
  )
}
